import React from 'react';
import {
    isEndAlignedTimeseriesTick,
    timeseriesTickOverflowsPlot,
} from './dateUtils';

/** Hide labels on side-by-side comparison bars narrower than this (tooltip shows details). */
export const VERTICAL_BAR_LABEL_MIN_WIDTH_COMPARISON = 34;

/** Minimum bar width for single-series labels (lower so Overview widgets still show values). */
export const VERTICAL_BAR_LABEL_MIN_WIDTH_SINGLE = 16;

/** Widget-size bar / donut value labels. */
export const BAR_VALUE_FONT = 10;
/** Fullscreen value labels when the plot (and bars) actually got wider. */
export const BAR_VALUE_FONT_FULLSCREEN = 13;

/**
 * Value-label size. Fullscreen only bumps when bars got wider; min-width
 * ranking charts that still scroll keep the widget size.
 */
export function chartValueFontSize(
    isFullscreen: boolean,
    barsWider = true,
    widgetSize = BAR_VALUE_FONT,
): number {
    return isFullscreen && barsWider ? BAR_VALUE_FONT_FULLSCREEN : widgetSize;
}

/** Y-axis tick size. Always allowed to grow in fullscreen (not tied to bar width). */
export function yAxisTickFontSize(isFullscreen: boolean, widgetSize = 11): number {
    return isFullscreen ? BAR_VALUE_FONT_FULLSCREEN : widgetSize;
}

/** Reserved Y-axis strip. Fullscreen type is larger, so the band is wider. */
export const Y_AXIS_WIDTH = 55;
export const Y_AXIS_WIDTH_FULLSCREEN = 66;

export function yAxisWidth(isFullscreen: boolean): number {
    return isFullscreen ? Y_AXIS_WIDTH_FULLSCREEN : Y_AXIS_WIDTH;
}

/** Approximate advance width of bold chart label text, per character, as a fraction of font size. */
const LABEL_CHAR_WIDTH_RATIO = 0.65;

/**
 * How far a clustered label may overhang its own bar before it collides with the neighbouring
 * bar's label. Charts cluster bars 2-4px apart, so the allowance stays small.
 */
const CLUSTERED_LABEL_OVERHANG = 3;

/** Estimated rendered width of a label; SVG text cannot be measured before it is drawn. */
export const estimateLabelWidth = (label: string, fontSize: number): number =>
    label.length * fontSize * LABEL_CHAR_WIDTH_RATIO;

export interface VerticalBarValueLabelOptions {
    formatValue: (value: number) => string;
    fill?: string;
    fontSize?: number;
    hideZero?: boolean;
    /** Render labels for zero-height bars (e.g. real $0 comparison bars). */
    allowZeroHeight?: boolean;
    /** Per-bar width below which the label is omitted. Defaults to single-series threshold. */
    minWidth?: number;
    /**
     * Bars sit side by side inside a cluster (marketplace breakdown, prior + current pairs), so
     * labels wider than their own bar overlap the neighbour's. Hides those that do not fit.
     */
    clustered?: boolean;
}

/** Scale down label font on narrow bars so above-bar text still fits. */
export function getVerticalBarLabelFontSize(width: number, baseFontSize: number): number {
    if (width < 12) return Math.max(7, baseFontSize - 3);
    if (width < 16) return Math.max(7, baseFontSize - 2);
    if (width < 22) return Math.max(8, baseFontSize - 2);
    if (width < 28) return Math.max(8, baseFontSize - 1);
    return baseFontSize;
}

const POSITIVE_LABEL_OFFSET = 6;
const NEGATIVE_LABEL_OFFSET = 10;
/** Extra lift so line labels clear the hover dot (r: 5 + stroke). */
const LINE_POINT_LABEL_OFFSET = 12;

/** y position for a value label outside the bar (above if positive, below if negative). */
export function getVerticalBarLabelY(
    barTop: number,
    barBottom: number,
    value: number,
    _fontSize: number,
): { y: number; dominantBaseline?: 'hanging' | 'auto' | 'central' | 'alphabetic' } {
    if (value < 0) {
        return { y: barBottom + NEGATIVE_LABEL_OFFSET, dominantBaseline: 'hanging' };
    }
    return { y: barTop - POSITIVE_LABEL_OFFSET, dominantBaseline: 'alphabetic' };
}

/** Always place value labels outside vertical bars; smaller font when the bar is narrow. */
export function renderVerticalBarValueLabel(
    props: any,
    options: VerticalBarValueLabelOptions,
): React.ReactElement | null {
    const {
        formatValue,
        fill = '#4A5568',
        fontSize = BAR_VALUE_FONT,
        hideZero = true,
        allowZeroHeight = false,
        minWidth = VERTICAL_BAR_LABEL_MIN_WIDTH_SINGLE,
        clustered = false,
    } = options;
    const value = typeof props?.value === 'number' ? props.value : Number(props?.value);
    if (!Number.isFinite(value) || (hideZero && value === 0)) return null;

    const x = typeof props?.x === 'number' ? props.x : 0;
    const y = typeof props?.y === 'number' ? props.y : 0;
    const width = typeof props?.width === 'number' ? props.width : 0;
    const height = typeof props?.height === 'number' ? props.height : 0;
    if (width <= 0) return null;
    if (height === 0 && !allowZeroHeight) return null;
    if (width < minWidth) return null;

    const barTop = Math.min(y, y + height);
    const barBottom = Math.max(y, y + height);
    const effectiveFontSize = getVerticalBarLabelFontSize(width, fontSize);
    const label = formatValue(value);
    if (clustered && estimateLabelWidth(label, effectiveFontSize) > width + CLUSTERED_LABEL_OVERHANG) {
        return null;
    }

    const { y: labelY, dominantBaseline } = height === 0
        ? { y: y - POSITIVE_LABEL_OFFSET, dominantBaseline: 'alphabetic' as const }
        : getVerticalBarLabelY(barTop, barBottom, value, effectiveFontSize);

    return (
        <text
            x={x + width / 2}
            y={labelY}
            fill={fill}
            fontSize={effectiveFontSize}
            fontWeight="bold"
            textAnchor="middle"
            dominantBaseline={dominantBaseline}
            pointerEvents="none"
        >
            {label}
        </text>
    );
}

/** LabelList `content` for single-series bars. */
export const verticalBarLabelListContent = (
    formatValue: (value: number) => string,
    fontSize = BAR_VALUE_FONT,
    minWidth = VERTICAL_BAR_LABEL_MIN_WIDTH_SINGLE,
    clustered = false,
) => (props: any) => renderVerticalBarValueLabel(props, { formatValue, fontSize, minWidth, clustered });

/** LabelList `content` for side-by-side prior + current bars. */
export const comparisonBarLabelListContent = (
    formatValue: (value: number) => string,
    fontSize = BAR_VALUE_FONT,
    clustered = false,
) => (props: any) => renderVerticalBarValueLabel(props, {
    formatValue,
    fontSize,
    minWidth: VERTICAL_BAR_LABEL_MIN_WIDTH_COMPARISON,
    hideZero: false,
    allowZeroHeight: true,
    clustered,
});

export const verticalBarTopLabelListProps = (
    formatValue: (value: number) => string,
    fontSize = BAR_VALUE_FONT,
    clustered = false,
) => ({
    content: verticalBarLabelListContent(formatValue, fontSize, VERTICAL_BAR_LABEL_MIN_WIDTH_SINGLE, clustered),
});

/** Inside-segment labels for stacked bars (like donut slice labels). */
export function renderStackedSegmentLabel(
    props: any,
    formatValue: (value: number) => string,
    fontSize = BAR_VALUE_FONT,
): React.ReactElement | null {
    const value = typeof props?.value === 'number' ? props.value : Number(props?.value);
    if (!Number.isFinite(value) || value === 0) return null;

    const x = typeof props?.x === 'number' ? props.x : 0;
    const y = typeof props?.y === 'number' ? props.y : 0;
    const width = typeof props?.width === 'number' ? props.width : 0;
    const height = typeof props?.height === 'number' ? props.height : 0;
    if (width <= 0 || height <= 0) return null;

    const label = formatValue(value);
    const centerY = y + height / 2;
    const labelX = x + width / 2;
    const minHorizontalHeight = Math.round(fontSize * 1.6);
    const minHorizontalWidth = Math.max(Math.round(fontSize * 2.4), estimateLabelWidth(label, fontSize));
    const minVerticalHeight = Math.max(Math.round(fontSize * 2.8), Math.round(label.length * fontSize * 0.7));
    const minVerticalWidth = Math.round(fontSize * 1.2);

    const fitsHorizontal = height >= minHorizontalHeight && width >= minHorizontalWidth;
    if (fitsHorizontal) {
        return (
            <text
                x={labelX}
                y={centerY}
                fill="white"
                fontSize={fontSize}
                fontWeight="bold"
                textAnchor="middle"
                dominantBaseline="central"
                pointerEvents="none"
            >
                {label}
            </text>
        );
    }

    const fitsVertical = height >= minVerticalHeight && width >= minVerticalWidth;
    if (fitsVertical) {
        return (
            <text
                x={labelX}
                y={centerY}
                fill="white"
                fontSize={fontSize}
                fontWeight="bold"
                textAnchor="middle"
                dominantBaseline="central"
                transform={`rotate(-90, ${labelX}, ${centerY})`}
                pointerEvents="none"
            >
                {label}
            </text>
        );
    }

    return null;
}

/** LabelList `content` for stacked bar segments. */
export const stackedSegmentLabelListContent = (
    formatValue: (value: number) => string,
    fontSize = BAR_VALUE_FONT,
) => (props: any) => renderStackedSegmentLabel(props, formatValue, fontSize);

/** Inset the first line point from the Y-axis so the first value label can sit above it. */
export const LINE_CHART_PLOT_LEFT_PAD = 24;
export const LINE_CHART_PLOT_LEFT_PAD_FULLSCREEN = 32;

export function lineChartPlotLeftPad(isFullscreen: boolean): number {
    return isFullscreen ? LINE_CHART_PLOT_LEFT_PAD_FULLSCREEN : LINE_CHART_PLOT_LEFT_PAD;
}

export interface LinePointDateTickOptions {
    labels: string[];
    fontSize: number;
    angle: number;
    textAnchor: 'middle' | 'end';
    svgWidth: number;
}

export interface LinePointValueLabelOptions {
    formatValue: (value: number) => string;
    fill?: string;
    fontSize?: number;
    hideZero?: boolean;
    pointCount: number;
    /** Same end-aligned cadence as the timeseries date axis. */
    tickStep: number;
    /** Fallback plot left when parentViewBox.x is missing. */
    plotLeft?: number;
    /** Hide when the date tick at this index would itself be clipped. */
    dateTick?: LinePointDateTickOptions;
}

/**
 * Value label above (or below, if negative) a single-series line point.
 * Shown only on points that already have a visible date tick, and hidden
 * when the centered label would cross the Y-axis / plot left edge.
 */
export function renderLinePointValueLabel(
    props: any,
    options: LinePointValueLabelOptions,
): React.ReactElement | null {
    const {
        formatValue,
        fill = '#4A5568',
        fontSize = BAR_VALUE_FONT,
        hideZero = true,
        pointCount,
        tickStep,
        plotLeft: plotLeftFallback,
        dateTick,
    } = options;
    const value = typeof props?.value === 'number' ? props.value : Number(props?.value);
    if (!Number.isFinite(value) || (hideZero && value === 0)) return null;

    const index = typeof props?.index === 'number' ? props.index : 0;
    if (!isEndAlignedTimeseriesTick(index, pointCount, tickStep)) return null;

    const x = typeof props?.x === 'number' ? props.x : 0;
    const y = typeof props?.y === 'number' ? props.y : 0;
    if (dateTick && timeseriesTickOverflowsPlot(
        x,
        dateTick.labels[index] ?? '',
        dateTick.fontSize,
        dateTick.angle,
        dateTick.textAnchor,
        dateTick.svgWidth,
    )) {
        return null;
    }

    const label = formatValue(value);
    const plotLeft = typeof props?.parentViewBox?.x === 'number'
        ? props.parentViewBox.x
        : (plotLeftFallback ?? 0);
    if (x - estimateLabelWidth(label, fontSize) / 2 < plotLeft + 2) return null;

    const below = value < 0;
    return (
        <text
            x={x}
            y={below ? y + LINE_POINT_LABEL_OFFSET : y - LINE_POINT_LABEL_OFFSET}
            fill={fill}
            fontSize={fontSize}
            fontWeight="bold"
            textAnchor="middle"
            dominantBaseline={below ? 'hanging' : 'alphabetic'}
            pointerEvents="none"
        >
            {label}
        </text>
    );
}

/** LabelList `content` for single-series line points. */
export const linePointLabelListContent = (
    formatValue: (value: number) => string,
    fontSize: number,
    options: Omit<LinePointValueLabelOptions, 'formatValue' | 'fontSize'>,
) => (props: any) => renderLinePointValueLabel(props, { formatValue, fontSize, ...options });
